import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { tarifsPourEnfant } from '../lib/orders/tarifs.gateway';
import { periodesAVenir } from '../lib/catalogue';

export interface Activity {
  id: string;
  stage: {
    id: string;
    title: string;
    description: string;
    age_min: number;
    age_max: number;
    base_price: number;
    image_url: string | null;
    /** `plaine`, `stage`… : l'ordre des cartes dans une semaine. */
    type_activite?: string | null;
  };
  center: {
    id: string;
    name: string;
    address: string;
    postal_code: string;
    city: string;
  };
  start_date: string;
  end_date: string;
  capacity: number;
  prix_normal: number;
  prix_reduit: number | null;
  prix_local: number | null;
  prix_local_reduit: number | null;
  periode: string;
  semaine: string | null;
  /** Les jours ouvrés de la semaine (4 quand un férié l'écourte). */
  nombre_jours?: number | null;
  remarques: string | null;
  visible_from: string | null;
  tarif_condition_id: string | null;
  calculated_main_price?: number;
  calculated_reduced_price?: number | null;
  current_registrations: number;
}

export interface ActivityFilters {
  kid_id?: string;
  center_id?: string;
  periode?: string;
  minAge?: number;
  maxAge?: number;
  dateStart?: string;
  dateEnd?: string;
}

interface ActivityState {
  activities: Activity[];
  currentActivity: Activity | null;
  filters: ActivityFilters;
  centers: { id: string; name: string; school_id: string | null }[];
  periodes: string[];
  isLoading: boolean;
  error: string | null;
  fetchActivities: (filters?: ActivityFilters) => Promise<void>;
  fetchActivity: (id: string) => Promise<void>;
  setFilters: (filters: ActivityFilters) => void;
  clearFilters: () => void;
  clearKidFilter: () => void;
  fetchCenters: () => Promise<void>;
  fetchPeriodes: () => Promise<void>;

}

function calculateAgeAtDate(birthDate: string, referenceDate: string): number {
  const birth = new Date(birthDate);
  const ref = new Date(referenceDate);

  let years = ref.getFullYear() - birth.getFullYear();
  let months = ref.getMonth() - birth.getMonth();

  if (months < 0) {
    years--;
    months += 12;
  } else if (months === 0 && ref.getDate() < birth.getDate()) {
    years--;
    months = 11;
  }

  if (months > 0 && ref.getDate() < birth.getDate()) {
    months--;
  }

  return parseFloat((years + (months / 12)).toFixed(1));
}

/**
 * Les sessions du catalogue pour des filtres donnés — âge et tarifs de
 * l'enfant compris quand `kid_id` est fourni.
 *
 * Sortie du magasin pour pouvoir être appelée une fois PAR ENFANT : un parent
 * de trois enfants voit les stages de chacun sur la même page, sans que le
 * filtre d'un enfant ne déborde sur la page d'accueil (qui lit ce magasin).
 */
export async function chargerSessions(activeFilters: ActivityFilters): Promise<Activity[]> {
  let query = supabase
    .from('sessions')
    .select(`
      id,
      stage_id,
      center_id,
      start_date,
      end_date,
      capacity,
      periode,
      prix_normal,
      prix_reduit,
      prix_local,
      prix_local_reduit,
      remarques,
      visible_from,
      tarif_condition_id,
      semaine,
      nombre_jours,
      current_registrations,
      stage:stages(
        id,
        title,
        description,
        age_min,
        age_max,
        base_price,
        image_url,
        type_activite
      ),
      center:centers(
        id,
        name,
        address,
        postal_code,
        city
      )
    `)
    .eq('active', true)
    /*
      L'archivage retire du catalogue. Il ne le faisait pas.

      Mesure le 8 septembre 2026 : les 263 sessions archivees portaient
      toutes `active = true`, et cette requete — celle du catalogue parent ET
      de la page d'accueil, via `PopularStages` — ne testait jamais
      `archived`. Sans degat jusqu'ici, parce qu'archive coincidait avec
      passe : aucune session archivee n'avait de date future. Mais archiver
      une semaine a venir, pour annuler un stage, ne la retirait PAS du site.

      Le filtre est ici et pas dans la RLS, deliberement : la page des
      inscriptions du parent joint `sessions` pour nommer ses stages passes
      (`RegistrationsPage`, `session:activity_id(...)`). Masquer les
      archivees au niveau de la base viderait l'historique des familles.
      C'est le CATALOGUE qui doit les taire, pas la table.
    */
    .eq('archived', false)
    .order('start_date');

  // Filter out past activities
  const today = new Date().toISOString().split('T')[0]; // Format as YYYY-MM-DD
  query = query.gte('end_date', today);
  
  // Apply filters if provided
  if (activeFilters.center_id) {
    query = query.eq('center_id', activeFilters.center_id);
  }
  
  if (activeFilters.periode) {
    query = query.eq('periode', activeFilters.periode);
  }

  /*
    Les stages a venir sont desormais MONTRES, avec la date a laquelle
    leurs inscriptions ouvrent — c'est la demande du 11 septembre 2026.
    Le filtre qui les ecartait est donc retire.

    Ce n'est pas lui qui les protegeait : la RLS ne les servait pas non
    plus, et c'est elle qui a change. Ce qui empeche desormais une
    inscription anticipee est un declencheur sur `registrations`, en base,
    que ni ce filtre ni son absence ne contournent.
  */
  
  const { data, error } = await query;
  
  if (error) throw error;

  /*
    Les journées pédagogiques ne sont pas des stages : elles s'inscrivent
    par leur propre page, réservées aux élèves d'une école.
  */
  let filteredData = (data || []).filter(
    (session) => (session as { stage?: { type_activite?: string } | null }).stage?.type_activite !== 'jpa',
  );
  
  // If kid_id is provided, filter by age and calculate prices
  if (activeFilters.kid_id) {
    // Get kid's data for price calculation
    const { data: kidData } = await supabase
      .from('kids')
      .select('date_naissance, cpostal, ecole')
      .eq('id', activeFilters.kid_id)
      .single();

    if (kidData) {
      /*
        L'age est une regle d'ecran : elle reste ici. Le prix est une regle
        d'argent : il vient de la base.

        Ce bloc appelait `getPrice()` une fois par session — donc une
        requete par session, et une REIMPLEMENTATION de la regle du tarif
        local qui ne testait pas `tarif_conditions.active`. Le catalogue
        pouvait donc afficher un tarif local que la facture n'appliquerait
        pas. `resolve_prices_for_kid` repond pour toutes les semaines en un
        aller-retour, et c'est la fonction que la facture utilise.
      */
      const eligibles = filteredData.filter((session) => {
        const ageAtStart = calculateAgeAtDate(kidData.date_naissance, session.start_date);
        return ageAtStart >= session.stage.age_min && ageAtStart < session.stage.age_max;
      });

      const [normaux, reduits] = await Promise.all([
        tarifsPourEnfant(activeFilters.kid_id, eligibles.map((s: any) => s.id), false),
        tarifsPourEnfant(activeFilters.kid_id, eligibles.map((s: any) => s.id), true),
      ]);

      filteredData = eligibles.map((session: any) => {
        const n = normaux.get(session.id);
        const r = reduits.get(session.id);
        return {
          ...session,
          calculated_main_price: n ? n.amount_cents / 100 : session.prix_normal,
          calculated_reduced_price: r ? r.amount_cents / 100 : session.prix_reduit,
          current_registrations: session.current_registrations || 0
        };
      });
    }
  } else {
    /*
      Sans enfant choisi, aucun tarif local ne peut s'appliquer : le tarif
      local depend du code postal et de l'ecole de l'ENFANT. On affiche donc
      le tarif de base, sans aller le demander — l'ancien code faisait une
      requete par session pour arriver au meme resultat.
    */
    filteredData = filteredData.map((session: any) => ({
      ...session,
      calculated_main_price: session.prix_normal,
      calculated_reduced_price: session.prix_reduit,
      current_registrations: session.current_registrations || 0
    }));
  }

  return filteredData as unknown as Activity[];
}

export const useActivityStore = create<ActivityState>((set, get) => ({
  activities: [],
  currentActivity: null,
  filters: {},
  centers: [],
  periodes: [],
  isLoading: false,
  error: null,
  
  fetchActivities: async (filters = {}) => {
    set({ isLoading: true, error: null });
    try {
      const activeFilters = { ...get().filters, ...filters };
      const filteredData = await chargerSessions(activeFilters);
      set({ activities: filteredData });
    } catch (error: any) {
      console.error('Error fetching activities:', error);
      set({ error: error.message || 'Une erreur est survenue lors du chargement des activités' });
    } finally {
      set({ isLoading: false });
    }
  },
  
  fetchActivity: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('sessions')
        .select(`
          id,
          stage_id,
          center_id,
          start_date,
          end_date,
          capacity,
          periode,
          prix_normal,
          prix_reduit,
          prix_local,
          prix_local_reduit,
          remarques,
          visible_from,
          tarif_condition_id,
          semaine,
          current_registrations,
          stage:stages(
            id,
            title,
            description,
            age_min,
            age_max,
            base_price,
            image_url
          ),
          center:centers(
            id,
            name,
            address,
            postal_code,
            city
          )
        `)
        .eq('id', id)
        .single();
        
      if (error) throw error;

      const activityWithCount = {
        ...data,
        current_registrations: data.current_registrations || 0
      };

      set({ currentActivity: activityWithCount });
    } catch (error: any) {
      console.error('Error fetching activity:', error);
      set({ error: error.message || 'Une erreur est survenue lors du chargement de l\'activité' });
    } finally {
      set({ isLoading: false });
    }
  },
  
  setFilters: (filters) => {
    set({ filters: { ...get().filters, ...filters } });
    get().fetchActivities(filters);
  },
  
  clearFilters: () => {
    set({ filters: {} });
    get().fetchActivities({});
  },
  
  clearKidFilter: () => {
    set(state => ({ 
      filters: { 
        ...state.filters, 
        kid_id: undefined 
      } 
    }));
  },
  
  fetchCenters: async () => {
    try {
      const { data, error } = await supabase
        .from('centers')
        .select('*')
        .eq('active', true)
        .eq('archived', false)
        .order('name');

      if (error) throw error;
      /*
        Les stages ne se programment que dans les centres de vacances : un lieu
        de journées pédagogiques seulement n'a rien à filtrer ici. `*` plutôt
        que la colonne nommée, qui n'existe qu'après la migration des journées
        pédagogiques.
      */
      const lignes = (data ?? []) as { id: string; name: string; centre_de_vacances?: boolean; school_id?: string | null }[];
      set({
        centers: lignes
          .filter((c) => c.centre_de_vacances !== false)
          // `school_id` : l'école du centre, pour présélectionner le centre des enfants
          .map((c) => ({ id: c.id, name: c.name, school_id: c.school_id ?? null })),
      });
    } catch (error: any) {
      console.error('Error fetching centers:', error);
      set({ error: error.message || 'Une erreur est survenue lors du chargement des centres' });
    }
  },
  
  fetchPeriodes: async () => {
    try {
      const { data, error } = await supabase
        .from('sessions')
        .select('periode, start_date, end_date, stage:stages(type_activite)')
        .eq('active', true)
        .eq('archived', false)
        .gte('end_date', new Date().toISOString().slice(0, 10));

      if (error) throw error;

      /*
        Les journées pédagogiques ne sont pas une période de stages. Puis les
        périodes à venir seulement, de la plus proche à la plus lointaine : la
        première est présélectionnée sur la page des stages. Avant, toutes les
        périodes passées y figuraient, dans l'ordre alphabétique.
      */
      type Ligne = {
        periode: string | null;
        start_date: string;
        end_date: string;
        stage: { type_activite?: string } | { type_activite?: string }[] | null;
      };
      const deStages = ((data ?? []) as Ligne[]).filter((l) => {
        const stage = Array.isArray(l.stage) ? l.stage[0] : l.stage;
        return stage?.type_activite !== 'jpa';
      });
      set({ periodes: periodesAVenir(deStages) });
    } catch (error: any) {
      console.error('Error fetching periods:', error);
      set({ error: error.message || 'Une erreur est survenue lors du chargement des périodes' });
    }
  }
}));